// Drains the offline expense queue when the network returns.
// Singleton — wired into `App` once. Listens to `online` events
// + a 30s heartbeat (some browsers don't fire `online` reliably,
// e.g. when toggling airplane mode quickly).

import { presignAndPut, finalizeDocument } from "@/lib/uploader";
import { listAll, update, type QueuedExpense } from "./queue";

const HEARTBEAT_MS = 30_000;
const MAX_ATTEMPTS = 3;

let started = false;
let draining = false;

export function startSyncRunner(): void {
  if (started) return;
  started = true;
  window.addEventListener("online", () => {
    void drainQueue();
  });
  setInterval(() => {
    if (navigator.onLine) void drainQueue();
  }, HEARTBEAT_MS);
  // Kick once on boot in case the user opens the app already online
  // with stuff queued from a previous session.
  if (navigator.onLine) void drainQueue();
}

export async function drainQueue(): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    const items = await listAll();
    for (const item of items) {
      if (item.state === "synced" || item.state === "failed") continue;
      await drainOne(item);
    }
  } finally {
    draining = false;
  }
}

async function drainOne(item: QueuedExpense): Promise<void> {
  if (!navigator.onLine) return;
  try {
    let documentId: number | null = null;
    if (item.receipt?.kind === "uploaded") {
      documentId = item.receipt.documentId;
    } else if (item.receipt?.kind === "queued") {
      await update(item.id, { state: "uploading_receipt" });
      const file = new File([item.receipt.blob], item.receipt.filename, {
        type: item.receipt.mimeType,
      });
      const { pendingUploadId } = await presignAndPut({
        docType: "receipt",
        propertyId: item.payload.propertyId,
        file,
      });
      const finalized = await finalizeDocument(pendingUploadId, file.name);
      documentId = finalized.documentId;
      await update(item.id, {
        receipt: { kind: "uploaded", documentId: finalized.documentId },
      });
    }

    await update(item.id, { state: "submitting" });
    const r = await fetch(
      `/api/properties/${item.payload.propertyId}/expenses`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          // Phase E adds server-side dedupe against this header;
          // until then it's a no-op (server ignores unknown headers).
          "X-Client-Request-Id": item.id,
        },
        body: JSON.stringify({
          phaseId: item.payload.phaseId,
          accountId: item.payload.accountId,
          amountCents: item.payload.amountCents,
          occurredOn: item.payload.occurredOn,
          vendor: item.payload.vendor,
          memo: item.payload.memo,
          documentKinds: item.payload.documentKinds,
          documentIds: documentId != null ? [documentId] : undefined,
          matchedEstimateId: item.payload.matchedEstimateId,
          paymentMethod: item.payload.paymentMethod,
        }),
      },
    );
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.message ?? `submit failed (${r.status})`);
    }
    await update(item.id, {
      state: "synced",
      lastError: null,
    });
  } catch (err) {
    const attempts = item.attempts + 1;
    const lastError = err instanceof Error ? err.message : String(err);
    if (attempts >= MAX_ATTEMPTS) {
      await update(item.id, { state: "failed", attempts, lastError });
    } else {
      await update(item.id, { state: "pending", attempts, lastError });
    }
  }
}

export async function retryFailed(id: string): Promise<void> {
  await update(id, { state: "pending", attempts: 0, lastError: null });
  if (navigator.onLine) void drainQueue();
}
